/**
 * Put a record into an in-memory list by id — replacing, never duplicating.
 *
 * Every store's create path used to append after its `await saveRecord(...)`.
 * If a `load()` ran in between — a sync pull finishing, another screen
 * hydrating the store — it read the new record from IndexedDB, and the append
 * then added it a second time: the list showed two of the same checklist until
 * the next reload. Found by the check-flights e2e test, intermittently, which
 * is the shape of a race.
 */
export function withRecord<T extends { id: string }>(
	list: readonly T[],
	item: T,
	at: 'start' | 'end' = 'end',
): T[] {
	const others = list.filter((entry) => entry.id !== item.id)
	return at === 'start' ? [item, ...others] : [...others, item]
}

/** `withRecord` for several at once, in order. */
export function withRecords<T extends { id: string }>(list: readonly T[], items: readonly T[]): T[] {
	const ids = new Set(items.map((item) => item.id))
	return [...list.filter((entry) => !ids.has(entry.id)), ...items]
}

/**
 * Re-read a list from storage **without throwing away what is being typed**.
 *
 * ## The bug this exists for
 *
 * Reported as *"a veces escribo cosas y se me borran"* on the flight plan.
 * Every store's `load()` assigned a fresh array read from Dexie, and
 * `hydrateStores()` in `App.vue` calls `load()` on every sync that pulled
 * anything — which is sign-in, regaining connectivity, coming back to the
 * foreground, and the sync button. On a phone that changes network every few
 * minutes that is often.
 *
 * The screen edits the store's own record (`flights.activePlan` is a `computed`
 * into the array), so replacing the array swaps the object out from under the
 * form: everything typed since the last autosave — up to 1.2 seconds of it, and
 * more while offline — was in the discarded object, and the field visibly
 * reverted. Nothing failed and nothing was logged.
 *
 * ## The rule
 *
 * A record that has **not moved on** is kept as the object already in memory,
 * identity and all. `updatedAt` decides: the stored row replaces the in-memory
 * one only when it is genuinely newer, which is exactly the case where somebody
 * else's edit has arrived and last-write-wins says it should. A row whose stamp
 * is the same is the row this device last saved, and the object in memory is
 * that row plus whatever is currently under the pilot's finger.
 *
 * Records are still **added** and **removed** as the stored list says, so a
 * pull that brought a new plan shows it and one that deleted a plan loses it.
 *
 * Instants, not strings: two devices in different offsets write the same moment
 * as `10:00:00Z` and `07:00:00-03:00`, which compare differently as text. Same
 * reasoning as `resolveConflict` in `core/sync`.
 *
 * Pure. `order` is applied to the result so the caller's sort is not lost.
 */
export function mergeLoaded<T extends { id: string; updatedAt?: string }>(
	current: readonly T[],
	loaded: readonly T[],
	order?: (a: T, b: T) => number,
): T[] {
	const mine = new Map(current.map((entry) => [entry.id, entry]))
	const merged = loaded.map((row) => {
		const existing = mine.get(row.id)
		if (!existing) return row
		return isNewer(row.updatedAt, existing.updatedAt) ? row : existing
	})
	return order ? merged.sort(order) : merged
}

/** True when `candidate` is a strictly later instant than `against`. */
function isNewer(candidate: string | undefined, against: string | undefined): boolean {
	const left = Date.parse(candidate ?? '')
	const right = Date.parse(against ?? '')
	// An unparseable stamp on either side is not evidence of anything, and the
	// safe answer is to keep what is in memory — which may be being edited.
	if (!Number.isFinite(left) || !Number.isFinite(right)) return false
	return left > right
}
